import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography, TextField, Button, Paper, Alert, useTheme, useMediaQuery, IconButton } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../services/api';

const Profile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const attrs = user?.attributes || {};
  
  const [formData, setFormData] = useState({
    firstName: attrs.given_name || '',
    lastName: attrs.family_name || ''
  });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Update form data when user attributes change
  useEffect(() => {
    if (attrs.given_name || attrs.family_name) {
      setFormData({
        firstName: attrs.given_name || '',
        lastName: attrs.family_name || ''
      });
    }
  }, [attrs.given_name, attrs.family_name]);

  if (!user) return null;

  const handleBackToDashboard = () => {
    // Navigate back to dashboard based on user type
    const userType = attrs.custom_user_type || user.userType;
    if (userType === 'BUILDING_ADMIN') {
      navigate('/building-admin');
    } else {
      navigate('/dashboard');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess('');
    setError('');
    setLoading(true);
    try {
      // Update Cognito attributes (excluding phone number as it's non-editable)
      const cognitoUser = window.userPool?.getCurrentUser();
      if (cognitoUser) {
        await new Promise((resolve, reject) => {
          cognitoUser.getSession((err, session) => {
            if (err || !session.isValid()) return reject('No valid session');
            
            // Prepare attributes to update (excluding phone number)
            const attributesToUpdate = [
              { Name: 'given_name', Value: formData.firstName },
              { Name: 'family_name', Value: formData.lastName }
            ];
            
            cognitoUser.updateAttributes(attributesToUpdate, (err, result) => {
              if (err) reject(err.message);
              else resolve(result);
            });
          });
        });
      }
      // Update backend user record (excluding phone number)
      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName
      };
      
      await usersAPI.update(user.username, updateData);
      setSuccess(t('dashboard.profileUpdated'));
      
      // Update user state (excluding phone number)
      setUser({ 
        ...user, 
        attributes: { 
          ...attrs, 
          given_name: formData.firstName, 
          family_name: formData.lastName
        } 
      });
    } catch (err) {
      setError(err.message || t('dashboard.profileUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="mobile-content-spacing profile-section" sx={{ 
      width: '100%',
      maxWidth: isMobile ? '100%' : 500, 
      mx: 'auto', 
      mt: isMobile ? 0.5 : 1,
      px: isMobile ? 1 : 2,
      pb: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh'
    }}>
      <Paper sx={{ 
        p: isMobile ? 1.5 : 2.5,
        boxShadow: isMobile ? 1 : 2,
        borderRadius: 2,
        width: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flex: 1
      }} elevation={isMobile ? 1 : 2}>
        {/* Back button */}
        <IconButton
          onClick={handleBackToDashboard}
          sx={{
            position: 'absolute',
            top: isMobile ? 6 : 12,
            left: isMobile ? 6 : 12,
            color: 'primary.main',
            zIndex: 1
          }}
          size="small"
        >
          <ArrowBack />
        </IconButton>
        
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          sx={{ 
            textAlign: 'center',
            mb: isMobile ? 1.5 : 2,
            mt: isMobile ? 0.5 : 1,
            fontWeight: 600,
            color: 'primary.main'
          }}
        >
          Profile
        </Typography>
        
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? 2 : 2.5,
              mb: 2
            }}>
              <TextField
                label="First Name *"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                fullWidth
                required
                size="medium"
                variant="outlined"
                sx={{ 
                  '& .MuiInputLabel-root': {
                    fontSize: '16px',
                    fontWeight: 500
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '16px',
                    padding: '16px 14px'
                  },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: '#d0d0d0',
                      borderWidth: '1px'
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                      borderWidth: '2px'
                    }
                  }
                }}
              />
              <TextField
                label="Last Name *"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                fullWidth
                required
                size="medium"
                variant="outlined"
                sx={{ 
                  '& .MuiInputLabel-root': {
                    fontSize: '16px',
                    fontWeight: 500
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '16px',
                    padding: '16px 14px'
                  },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: '#d0d0d0',
                      borderWidth: '1px'
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                      borderWidth: '2px'
                    }
                  }
                }}
              />
              <TextField
                label="Phone Number *"
                value={attrs.phone_number || ''}
                fullWidth
                disabled
                size="medium"
                variant="outlined"
                sx={{ 
                  '& .MuiInputLabel-root': {
                    fontSize: '16px',
                    fontWeight: 500
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '16px',
                    padding: '16px 14px'
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#d0d0d0',
                      borderWidth: '1px'
                    }
                  },
                  '& .Mui-disabled': {
                    backgroundColor: '#f8f8f8',
                    '& .MuiInputBase-input': {
                      color: '#666666',
                      '-webkit-text-fill-color': '#666666'
                    }
                  }
                }}
              />
              <TextField
                label="Email"
                value={attrs.email || ''}
                fullWidth
                disabled
                size="medium"
                variant="outlined"
                sx={{ 
                  '& .MuiInputLabel-root': {
                    fontSize: '16px',
                    fontWeight: 500
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '16px',
                    padding: '16px 14px'
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#d0d0d0',
                      borderWidth: '1px'
                    }
                  },
                  '& .Mui-disabled': {
                    backgroundColor: '#f8f8f8',
                    '& .MuiInputBase-input': {
                      color: '#666666',
                      '-webkit-text-fill-color': '#666666'
                    }
                  }
                }}
              />
            </Box>
            
            {/* Alerts */}
            {success && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 2,
                  fontSize: '16px',
                  borderRadius: 2,
                  p: 2
                }}
              >
                {success}
              </Alert>
            )}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  fontSize: '16px',
                  borderRadius: 2,
                  p: 2
                }}
              >
                {error}
              </Alert>
            )}
            
            {/* Buttons */}
            <Box sx={{ 
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2,
              alignItems: 'stretch',
              mt: 1
            }}>
              <Button
                variant="outlined"
                onClick={handleBackToDashboard}
                sx={{ 
                  minHeight: 48,
                  fontSize: '16px',
                  fontWeight: 500,
                  borderRadius: 2,
                  flex: isMobile ? 'none' : 1,
                  textTransform: 'uppercase',
                  borderWidth: '2px',
                  '&:hover': {
                    borderWidth: '2px'
                  }
                }}
                fullWidth={isMobile}
              >
                BACK TO DASHBOARD
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                sx={{ 
                  minHeight: 48,
                  fontSize: '16px',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'uppercase',
                  flex: isMobile ? 'none' : 2,
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 12px rgba(25, 118, 210, 0.4)'
                  }
                }} 
                disabled={loading}
                fullWidth={isMobile}
              >
                {loading ? 'UPDATING...' : 'UPDATE PROFILE'}
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default Profile; 